/**
 * Runs `task` on the triggers that matter for "has something newer shipped?"
 * polling: when the document becomes visible, when the device regains
 * connectivity, and on a periodic timer while the app stays open.
 *
 * The browser pushes neither of the things we poll for — a newer service worker
 * or a newer build manifest — and iOS Safari in particular resumes an installed
 * PWA straight from its snapshot without re-checking anything. So the app has
 * to ask. A run is skipped while the tab is hidden or the device is offline,
 * where it could only waste work or fail.
 *
 * Pass `immediate` to also run once during setup — still subject to the same
 * hidden/offline guard, so a tab that mounts in the background does not burn a
 * request it cannot use.
 *
 * Returns a cleanup function that tears down the timer and every listener.
 */
export function scheduleWhileVisible(
  task: () => void,
  intervalMs: number,
  { immediate = false }: { immediate?: boolean } = {},
): () => void {
  const run = () => {
    if (document.visibilityState !== "visible" || !navigator.onLine) return;
    task();
  };

  if (immediate) run();

  const interval = setInterval(run, intervalMs);
  document.addEventListener("visibilitychange", run);
  window.addEventListener("online", run);

  return () => {
    clearInterval(interval);
    document.removeEventListener("visibilitychange", run);
    window.removeEventListener("online", run);
  };
}
